import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { UseOutletContextProviderQuote } from './ProviderQuote';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { getError } from '../../../../languages/translations/response';
import { ProviderQuoteDA } from '../../../../services/admin/provider.quote.service';
import { Error } from '../../../../models/error.model';
import { CompanyProviderQuotes } from '../../../../models/company.provider.quotes.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { ProviderQuote } from '../../../../models/provider.quote.model';
import { formatMoney } from '../../../../scripts/format.value.script';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { colorStatusProviderQuote } from '../../../../scripts/badge.color.script';
import { normalizeStatus, statusDocumentValue } from '../../../../libraries/status.document.library';
import { isVatSubject } from '../../../../scripts/financial.calculation.script';
import { sortColumn } from '../../../../scripts/table.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { usePaginationUrl } from '../../../../hooks/usePaginationUrl';

import AppSortTable from '../../../../components/element/SortTable';
import AppPagination from '../../../../components/element/Pagination';
import AppPreloader from '../../../../components/Preloader';
import AppNone from '../../../../components/element/None';
import ButtonVouchers from '../../../../components/button/ButtonVouchers';
import AppModalProviderQuoteExport from '../../../../components/modal/ModalProviderQuoteExport';
import { modalShow } from '../../../../utils/modal.util';

export interface ProviderQuoteListPageProps {};

let errorResponse: Error, providerQuotesResponse: CompanyProviderQuotes;

const ProviderQuoteListPage: React.FunctionComponent<ProviderQuoteListPageProps> = props => {
  const {setRoute, companyForUser, settingsForCompany} = UseOutletContextProviderQuote()
  const {lang} = StateLanguage()
  const { page: urlPage, setPage: setUrlPage } = usePaginationUrl()

  const [mounted, setMounted] = useState(false)
  const [filter, setFilter] = useState({status: '', sort_field: '', sort_mode: '', search: ''})
  const [pagination, setPagination] = useState({current: 1, limit: 20, total: 0, pages: [1] as number[]})
  const [providerQuotes, setProviderQuotes] = useState<ProviderQuote[] | null>(null)
  const vatSubject = isVatSubject(settingsForCompany, companyForUser);

  const filterRef = useRef(filter)
  filterRef.current = filter

  const [searchText, setSearchText] = useState(filter.search)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current) }
  }, [])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchText(value)

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)

    searchTimerRef.current = setTimeout(() => {
      setFilter({...filter, search: value})
      if (companyForUser) loadProviderQuotes(companyForUser.id, 1)
    }, 400)
  }

  const loadProviderQuotes = async (id_company: string, page: number) => {
    const f = filterRef.current
    await ProviderQuoteDA.getProviderQuotes(id_company, f.status, f.sort_field, f.sort_mode, page, pagination.limit, f.search).then( (response) => {
      if (response.status === 200) {
        providerQuotesResponse = response.data

        const items = (providerQuotesResponse.items ?? providerQuotesResponse.provider_quotes ?? []).map((item: any) => ({ ...item, status: normalizeStatus(item.status) }))

        localStorage.setItem('nav_provider_quotes', JSON.stringify({ company_id: id_company, ids: items.map((item: any) => item.id) }))

        localStorage.setItem('filter_provider_quotes', JSON.stringify({
          company_id: id_company,
          status: f.status,
          sort_field: f.sort_field,
          sort_mode: f.sort_mode,
          search: f.search,
        }))

        const totalPages = providerQuotesResponse.pagination?.pages || 1

        if (page > totalPages && totalPages >= 1) {
          setUrlPage(totalPages)
          loadProviderQuotes(id_company, totalPages)
          return
        }

        let pagesOfPagination: number[] = []
        for (let i = 1; i <= totalPages; i++) {
          pagesOfPagination.push(i)
        }

        setProviderQuotes(items)
        setPagination({...pagination, current: providerQuotesResponse.pagination?.page || 1, total: providerQuotesResponse.pagination?.total || 0, pages: pagesOfPagination})
        setUrlPage(providerQuotesResponse.pagination?.page || 1)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadPagination = (current: number) => {
    if (!companyForUser) return null
    setUrlPage(current)
    loadProviderQuotes(companyForUser.id, current)
  }

  const executeSortList = (item: string) => {
    if (companyForUser) {
      const newFilter = {...filterRef.current}
      sortColumn(item, newFilter)
      setFilter(newFilter)
      setUrlPage(1)
      loadProviderQuotes(companyForUser.id, 1)
    }
  }

  const executeFilterStatus = (status: string) => {
    if (companyForUser) {
      setFilter({...filter, status: filter.status === status ? '' : status})
      setProviderQuotes(null)
      setUrlPage(1)
      loadProviderQuotes(companyForUser.id, 1)
    }
  }

  useEffect( () => {
    let filter_provider_quotes: any = (localStorage.getItem('filter_provider_quotes')) ? JSON.parse(localStorage.getItem('filter_provider_quotes')!) : null

    if (companyForUser) {
      const isMatch = filter_provider_quotes && filter_provider_quotes.company_id === companyForUser.id
      setFilter({
        status: isMatch ? filter_provider_quotes.status : '',
        sort_field: isMatch ? filter_provider_quotes.sort_field : '',
        sort_mode: isMatch ? filter_provider_quotes.sort_mode : '',
        search: isMatch ? (filter_provider_quotes.search ?? '') : ''
      })

      setProviderQuotes(null)
      loadProviderQuotes(companyForUser.id, urlPage)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.providerQuotes, branch: lang.labels.listProviderQuotes}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && providerQuotes
        ?
        <div className="card card-flush">
          <div className="card-header border-0 pt-5">
            <div className="card-title">{lang.labels.listProviderQuotes}</div>
            <div className="card-toolbar">
              <div className="d-flex gap-2 align-items-center">
                <div className="position-relative me-2">
                  <span className="svg-icon svg-icon-3 position-absolute top-50 translate-middle-y ms-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor"/>
                      <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor"/>
                    </svg>
                  </span>
                  <input
                    type="text"
                    className="form-control form-control-sm ps-10"
                    style={{width: '200px'}}
                    placeholder={lang.labels.search}
                    value={searchText}
                    onChange={handleSearchChange}
                  />
                </div>
                {['received', 'accepted', 'declined', 'expired'].map(s => (
                  <button
                    key={s}
                    className={`btn btn-sm ${filter.status === s ? `btn-${colorStatusProviderQuote(s)}` : `btn-light-${colorStatusProviderQuote(s)}`}`}
                    type="button"
                    onClick={() => executeFilterStatus(s)}
                  >
                    {statusDocumentValue(lang, s)}
                  </button>
                ))}
                <button className="btn btn-sm btn-primary" type="button" onClick={() => modalShow('modal-provider-quote-export')}>
                  {lang.labels.export}
                </button>
              </div>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="w-125px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("created_date")}>
                          <span className="me-1">{lang.labels.documentDate}</span>
                          <AppSortTable attribute={"created_date"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-150px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("provider_quote_number")}>
                          <span className="me-1">{lang.labels.number}</span>
                          <AppSortTable attribute={"provider_quote_number"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-150px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("concerning")}>
                          <span className="me-1">{lang.labels.concerning}</span>
                          <AppSortTable attribute={"concerning"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-150px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("source_company")}>
                          <span className="me-1">{lang.labels.sourceCompany}</span>
                          <AppSortTable attribute={"source_company"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-125px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("valid_until")}>
                          <span className="me-1">{lang.labels.validity}</span>
                          <AppSortTable attribute={"valid_until"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("status")}>
                          <span className="me-1">{lang.labels.status}</span>
                          <AppSortTable attribute={"status"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      {vatSubject && <th className="min-w-100px text-end">
                        <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("amount.tax_total")}>
                          <span className="me-1">{lang.labels.vatAmount}</span>
                          <AppSortTable attribute={"amount.tax_total"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>}
                      <th className="min-w-100px text-end">
                        <div className="d-flex align-items-center justify-content-end text-nowrap cursor-pointer" onClick={() => executeSortList("amount.total")}>
                          <span className="me-1">{lang.labels.amountTotal}</span>
                          <AppSortTable attribute={"amount.total"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="w-50px text-center"></th>
                    </tr>
                  </thead>
                  { pagination.total > 0
                    ?
                    <tbody className="text-secondary fw-bold">
                      { providerQuotes.map (( (item, index) => { return (
                        <tr key={index} className="row-dashed">
                          <td className="text-nowrap">
                            <AppNone condition={true} display={dateFormat(lang.code, item.created_date)}></AppNone>
                          </td>
                          <td>
                            <Link to={"/main/purchase/quote/view/" + item.id} className="fw-bolder text-nowrap text-dark text-hover-primary">{item.provider_quote_number}</Link>
                          </td>
                          <td>
                            <AppNone condition={true} display={item.concerning ?? ''}></AppNone>
                          </td>
                          <td>
                            <AppNone condition={true} display={item.source_company?.name ?? ''}></AppNone>
                          </td>
                          <td className="text-nowrap">
                            {item.valid_until
                              ? <AppNone condition={true} display={dateFormat(lang.code, item.valid_until)}></AppNone>
                              : <span className="text-muted">-</span>
                            }
                          </td>
                          <td>
                            <span className={`badge badge-light-${colorStatusProviderQuote(item.status)} fs-7`}>{statusDocumentValue(lang, item.status)}</span>
                          </td>
                          {vatSubject && <td className="text-end text-nowrap">{item.currency?.code ?? ''} {formatMoney(item.amount?.tax_total ?? 0)}</td>}
                          <td className="text-end text-nowrap">
                            <AppNone condition={true} display={`${item.currency?.code ?? ''} ${formatMoney(item.amount?.total ?? 0)}`}></AppNone>
                            {item.foreign_currency?.code && <div className="fs-9 fw-normal text-muted">{item.foreign_currency.code} {formatMoney(item.foreign_currency.amount?.total ?? 0)}</div>}
                          </td>
                          <td className="text-center">
                            <ButtonVouchers vouchers={item.vouchers} />
                          </td>
                        </tr>
                      )}))}
                    </tbody>
                    :
                    <tbody>
                      <tr className="text-secondary fw-bold">
                        <td colSpan={8 + (vatSubject ? 1 : 0)} className="text-center">{lang.labels.noRecordsFound}</td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
              <AppPagination showOption={false} pagination={pagination} loadPagination={loadPagination}></AppPagination>
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalProviderQuoteExport companyForUser={companyForUser} providerQuotes={providerQuotes} filter={filter} vatSubject={vatSubject}></AppModalProviderQuoteExport>
    </div>
  )
}

export default ProviderQuoteListPage;
